import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useEffect } from 'react';
import { parseLocation } from 'automation-ui-routing/utils';
import NextRecommendedActionCard from 'automation-ui-canvas/components/canvas/next-action-recommender/components/NextRecommendedActionCard';
import { getIsLastEditedAction } from 'automation-ui-canvas/selectors/flowSelectors';
import { useNextActionRecommender } from 'automation-ui-canvas/components/canvas/next-action-recommender/hooks/useNextActionRecommender';
import { getPreviousActionId } from 'automation-ui-action-features/records/recordsUtils';
import { getHighestProbableActionTypeId } from 'automation-ui-canvas/components/canvas/next-action-recommender/utils/getHighestProbableActionTypeId';
import { getActionMetadataConfigFromActionTypeIdWithoutSubtypes } from 'automation-ui-action-features/actions/ActionMetadataConfigUtils';
import { routeToSpecificAction } from 'automation-ui-canvas/utils/routingUtils';
import { DETAIL_MIN_ZOOM_LEVEL } from 'automation-ui-flow-core/canvas/graphConstants';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { getActionIconAndColor } from 'automation-ui-action-features/utils/actionThemeUtils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Action from 'automation-ui-action-features/records/Action';
import * as localStorageActions from 'automation-ui-canvas/actions/LocalStorageActions';
import PortalIdParser from 'PortalIdParser';
import { isEdgeOfBranchAction, isAllowedRecommendationEdgeForBranchAction } from 'automation-ui-canvas/components/canvas/next-action-recommender/utils/isAllowedRecommendationEdgeForBranchAction';
import { USER_ATTRIBUTE_HIDE_AI_NEXT_ACTION_RECOMMENDATIONS } from 'automation-ui-canvas/constants/UserSettingKeys';
import { useUserSettingByKey } from 'automation-ui-core/hooks/userSettings/userSettingHooks';
import { useLazyQuery } from 'data-fetching-client';
import { SEND_NEXT_ACTION_RECOMMENDATION_FEEDBACK } from 'automation-ui-canvas/components/canvas/next-action-recommender/queries/sendNextActionRecommendationFeedback';
import { jsx as _jsx } from "react/jsx-runtime";
const NextRecommendedAction = ({
  edgeId,
  flow,
  flowId,
  isLastEditedAction,
  isParentEdgeCreatingAction,
  history,
  zoomLevel = 1,
  mergeDeepWithLocalStorage,
  isDismissedRecommendation,
  disableRecommendationForEdge
}) => {
  var _flow$getObjectTypeId;
  const trackEvent = useTrackEvent();
  const [sendFeedback] = useLazyQuery(SEND_NEXT_ACTION_RECOMMENDATION_FEEDBACK);
  const layout = zoomLevel < DETAIL_MIN_ZOOM_LEVEL ? 'zoomed-out' : 'default';
  const prevActionId = getPreviousActionId(edgeId);
  const {
    ancestors = []
  } = flow ? flow.findAllAncestorsAndDecendantsOfAction(prevActionId, true) : {};
  const ancestorActionTypeIds = ancestors.slice(0, 4).map(action => action.getActionTypeId()).filter(id => id !== null) // Filter out null values and narrow type to string[]
  .reverse();
  const recommendationsAreDisabledOrLoading = useUserSettingByKey(USER_ATTRIBUTE_HIDE_AI_NEXT_ACTION_RECOMMENDATIONS);
  const recommendationsAreDisabled = recommendationsAreDisabledOrLoading === undefined ? true : recommendationsAreDisabledOrLoading;
  const {
    actionTypeIdInnerIdsToScores,
    chirpAiContextObject,
    shouldShowActionRecommendation
  } = useNextActionRecommender({
    ancestorActionTypeIds,
    flowObjectTypeId: (_flow$getObjectTypeId = flow === null || flow === void 0 ? void 0 : flow.getObjectTypeId()) !== null && _flow$getObjectTypeId !== void 0 ? _flow$getObjectTypeId : null,
    disableRecommendationOverride: !flow || !edgeId || !isLastEditedAction || disableRecommendationForEdge || isParentEdgeCreatingAction || isDismissedRecommendation || recommendationsAreDisabled
  });
  const {
    actionTypeId
  } = getHighestProbableActionTypeId(actionTypeIdInnerIdsToScores);
  const maybeActionMetadata = getActionMetadataConfigFromActionTypeIdWithoutSubtypes(actionTypeId || '');
  useEffect(() => {
    if (!shouldShowActionRecommendation) {
      return;
    }
    trackEvent(Trackables.ADD_RECOMMENDED_ACTION_SHOWN, {
      actionTypeId,
      // Reverse the ancestorActionTypeIds so the first action is the immediate parent
      source: ancestorActionTypeIds ? [...ancestorActionTypeIds].reverse() : []
    });
  }, [shouldShowActionRecommendation, actionTypeId, ancestorActionTypeIds, trackEvent]);
  if (!shouldShowActionRecommendation || !maybeActionMetadata || !chirpAiContextObject) {
    return null;
  }
  const actionToPreview = new Action({
    actionType: maybeActionMetadata.type
  });
  const {
    iconName,
    iconUrl
  } = getActionIconAndColor({
    action: actionToPreview,
    // Extension definitions are not needed for the MVP of the recommendation card
    // preview. The underlying ML model only supports built in actions for now
    extensionDefinitions: {}
  });
  const handleOnClickAddAction = () => {
    var _maybeActionMetadata$;
    trackEvent(Trackables.ADD_RECOMMENDED_ACTION_CLICKED, {
      actionTypeId,
      // Reverse the ancestorActionTypeIds so the first action is the immediate parent
      source: ancestorActionTypeIds ? [...ancestorActionTypeIds].reverse() : []
    });
    void sendFeedback({
      variables: {
        chirpAiContextObject,
        positiveFeedback: true,
        explicitFeedback: false
      }
    });
    routeToSpecificAction({
      history,
      edgeId,
      flowId: (flow === null || flow === void 0 ? void 0 : flow.getFlowId()) || -1,
      configId: (_maybeActionMetadata$ = maybeActionMetadata === null || maybeActionMetadata === void 0 ? void 0 : maybeActionMetadata.type) !== null && _maybeActionMetadata$ !== void 0 ? _maybeActionMetadata$ : null,
      extraParams: {
        type: '',
        addedByTooltip: 'next_action_recommender'
      },
      flowType: flow === null || flow === void 0 ? void 0 : flow.flowType
    });
  };
  const handleOnClickDismiss = () => {
    trackEvent(Trackables.DISMISS_RECOMMENDED_ACTION_CLICKED, {
      actionTypeId,
      // Reverse the ancestorActionTypeIds so the first action is the immediate parent
      source: ancestorActionTypeIds ? [...ancestorActionTypeIds].reverse() : []
    });
    void sendFeedback({
      variables: {
        chirpAiContextObject,
        positiveFeedback: false,
        explicitFeedback: false
      }
    });
    const portalId = PortalIdParser.get();
    mergeDeepWithLocalStorage({
      dismissedRecommendNextActionEdges: {
        [`${portalId}-${flowId}-${edgeId}`]: true
      }
    });
  };
  const handleOnClickThumbsUp = () => {
    void sendFeedback({
      variables: {
        chirpAiContextObject,
        positiveFeedback: true,
        explicitFeedback: true
      }
    });
  };
  const handleOnClickThumbsDown = feedbackMessage => {
    void sendFeedback({
      variables: {
        chirpAiContextObject,
        positiveFeedback: false,
        explicitFeedback: true,
        feedbackText: feedbackMessage
      }
    });
  };
  return /*#__PURE__*/_jsx(NextRecommendedActionCard, {
    chirpAiContextObject: chirpAiContextObject,
    headerIcon: iconName,
    headerImage: iconUrl,
    layout: layout,
    onClickAddAction: handleOnClickAddAction,
    onClickDismiss: handleOnClickDismiss,
    onClickThumbsUp: handleOnClickThumbsUp,
    onClickThumbsDown: handleOnClickThumbsDown,
    headerTitle: maybeActionMetadata === null || maybeActionMetadata === void 0 ? void 0 : maybeActionMetadata.getActionLabel(undefined, undefined, true),
    content: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "recommendedActionCard.description"
    })
  });
};

// Return the set of tooltips that would appear but have been dismissed
const getIsDismissedNextActionRecommendation = (localStorage, edgeId, flowId) => {
  const allDismissedRecommendations = localStorage.get('dismissedRecommendNextActionEdges');
  if (!allDismissedRecommendations) return false;
  const portalId = PortalIdParser.get();
  return allDismissedRecommendations.get(`${portalId}-${flowId}-${edgeId}`) || false;
};

/**
 * Performance Optimization Note:
 *
 * This component uses `connect` instead of hooks for critical performance reasons:
 *
 * 1. The UIDirectedGraph should only re-render when graph nodes change
 * 2. Using `connect` with mapStateToProps provides automatic memoization and
 *    shallow prop comparison, preventing unnecessary re-renders
 * 3. Direct store access through `connect` is more efficient than hook-based
 *    subscriptions in this case, as the flow gets re-rendered frequently
 *
 * Evaluating use of useSelector resulted in significantly more re-renders,
 * impacting canvas performance. The connect pattern ensures minimal updates
 * while maintaining access to the userEditedActionIds state.
 */
const mapStateToProps = (state, {
  edgeId,
  flow,
  flowId,
  isMulti,
  location
}) => {
  const parsedQuery = parseLocation(location);
  let selectedEdgeId = parsedQuery.edgeId;
  const activeFlowId = Number(parsedQuery.activeFlowId);
  const isDismissedRecommendation = getIsDismissedNextActionRecommendation(state.localStorage, edgeId, flowId);
  if (isMulti && activeFlowId !== flowId) {
    selectedEdgeId = null;
  }
  return {
    isParentEdgeCreatingAction: selectedEdgeId === edgeId,
    isLastEditedAction: getIsLastEditedAction(flowId, edgeId)(state),
    isDismissedRecommendation,
    disableRecommendationForEdge: isEdgeOfBranchAction(flow, edgeId) && !isAllowedRecommendationEdgeForBranchAction(edgeId)
  };
};
export default compose(withRouter, connect(mapStateToProps, {
  mergeDeepWithLocalStorage: localStorageActions.mergeDeepWithLocalStorage
}))(NextRecommendedAction);